<template>
  <div class="vAutocompleteStyles">
    <ValidationProvider
      :ref="validationProviderRef"
      :mode="veeValidateInteractionMode"
      :rules="rules"
      :vid="validationProviderRef"
      :debounce="validationDelay"
      :name="label"
    >
      <v-autocomplete
        :id="validationProviderRef"
        ref="autocomplete"
        v-model="mutableValue"
        slot-scope="{ errors }"
        :chips="multiple"
        :class="`extraClass ${noChevron ? 'no-chevron': ''}`"
        :deletableChips="multiple"
        :disabled="disabled"
        :errorMessages="errors"
        :itemText="itemText"
        :itemValue="itemValue"
        :items="items"
        :label="displayedLabel"
        :multiple="multiple"
        :required="isRequired"
        :searchInput.sync="searchInput"
        :attach="attach"
        v-bind="$attrs"
        :filter="customFilter"
        :data-cy="datacy"
        @input="onInput"
        @change="onChange"
      >
        <template
          :slot="withCustomItemText ? 'item' : undefined"
          slot-scope="{ item }"
        >
          <slot
            name="customItemText"
            :item="{item}"
          />
        </template>
        <template v-slot:selection="selectionData">
          <slot
            name="selection"
            v-bind="selectionData"
          >
            <v-chip
              v-if="multiple"
              v-bind="selectionData.attrs"
              :inputValue="selectionData.selected"
              :close="multiple && !(selectionData.item && selectionData.item.disabled)"
              @click="selectionData.select"
              @click:close="selectionData.parent.onChipInput(selectionData.item)"
            >
              {{ selectionData.item[itemText] }}
            </v-chip>
            <template v-else>
              {{ selectionData.item[itemText] }}
            </template>
          </slot>
        </template>
        <template v-slot:append-item>
          <slot name="withInfiniteScroll" />
        </template>
      </v-autocomplete>
      <SlotScopeWatcher
        :value="mutableValue"
        @change="(!isFirstValue || validateAtInitialization) && validate(mutableValue)"
      />
    </ValidationProvider>
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import validationProviderHelperMixin from '@/components/shared/widgets/forms/validationHelper/validationProviderHelperMixin';
import debounce from 'lodash/debounce';

export default {
    components: {
        ValidationProvider,
        SlotScopeWatcher,
    },
    mixins: [
        validationProviderHelperMixin,
    ],
    props: {
        label: {
            type: String,
            default: '',
        },
        rules: {
            type: Object,
            default: () => ({ required: false }),
        },
        value: {
            type: [String, Object, Array],
            default: null,
        },
        extraClass: {
            type: String,
            default: null,
        },
        itemText: {
            type: String,
            default: 'name',
        },
        itemValue: {
            type: String,
            default: 'id',
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        validateAtInitialization: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        items: {
            type: Array,
            default: () => [],
        },
        syncSearchInput: {
            type: Boolean,
            default: true,
        },
        withCustomItemText: {
            type: Boolean,
            default: false,
        },
        attach: {
            type: Boolean,
            default: false,
        },
        noChevron: {
            type: Boolean,
            default: false,
        },
        datacy: {
            type: String,
            default: 'cypress',
        },
    },
    data() {
        return {
            isFirstValue: true,
            mutableValue: this.value,
            searchInput: null,
            canSearch: true,
            debounceSearchInput: debounce(this.onSearchInput, 500),
        };
    },
    computed: {
        isRequired() {
            return Object.keys(this.rules).some(rule => (rule === 'required' && this.rules[rule]));
        },
        displayedLabel() {
            return `${this.label} ${this.isRequired ? '*' : ''}`;
        },
    },
    watch: {
        value: {
            handler() {
                this.isFirstValue = false;
                this.mutableValue = this.value;
                if (!!this.mutableValue && this.mutableValue !== '') {
                    this.$nextTick(() => {
                        this.validateValidationProvider().then(response => {
                            if (response.valid) this.validateValidationProvider(false);
                        });
                    });
                }
            },
            deep: true,
            immediate: true,
        },

        mutableValue: {
            handler(newValue) {
                if (newValue) {
                    this.searchInput = null;
                }
            },
        },


        searchInput: {
            handler(newValue, oldValue) {
                this.debounceSearchInput(newValue, oldValue);
            },
        },
    },
    methods: {
        customFilter(item, queryText, itemText) {
            const text = itemText.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const search = queryText.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

            return text.indexOf(search) > -1;
        },
        onInput(value) {
            // trigered anytime we select a value
            this.canSearch = false;
            this.$emit('input', value);
        },
        onChange(value) {
            // trigered only when the selected value changes
            this.canSearch = false;
            this.$emit('change', value);
        },
        onSearchInput(newValue, oldValue) {
            const condition1 = this.canSearch && newValue !== null && !this.mutableValue;
            const condition2 = this.canSearch && newValue !== null && oldValue !== null && this.mutableValue;
            if (condition1 || condition2) {
                // trigered when manually typing inside the field
                this.$emit('search', this.searchInput);
            }
            this.canSearch = true;
        },
    },
};
</script>


<style lang="scss">
    .blocChip {
        &__icon {
            font-size: 12px !important;
            color: white !important;
            margin-left: 5px;
            background-color: grey;
            border-radius: 50%;
            padding: 4px;
            width: max-content
        }
    }
    @media screen and (max-width: 600px) {
        .blocChip {
            width: max-content;
            height: max-content;

            &__text {
                white-space: nowrap;
                overflow: hidden;
                max-width: 90% !important;
            }
            &__icon {
                width: auto;
                right: 0
            }
        }
    }
    .vAutocompleteStyles{
        .v-input__append-inner {
            cursor: pointer;
        }
        .no-chevron .v-input__append-inner {
            display: none;
        }
    }
</style>
